// Banner CTA persistente arriba del topbar — invita al user a activar notificaciones
// push del SO. Estados: default (pide), denied (instrucciones), granted (no muestra).
// Dismiss: 7 días la primera vez, 30 días la segunda.

import { useState } from "react";
import { Bell, BellOff, X } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  isPushSupported,
  permissionState,
  activatePush,
  sendTestNotification,
} from "../lib/pushClient";

const DISMISS_KEY = "push_banner_dismissed_at";
const DISMISS_COUNT_KEY = "push_banner_dismiss_count";
const DAY_MS = 24 * 60 * 60 * 1000;

function shouldShowBanner() {
  if (!isPushSupported()) return false;
  if (permissionState() === "granted") return false;
  const dismissedAt = localStorage.getItem(DISMISS_KEY);
  const dismissCount = parseInt(localStorage.getItem(DISMISS_COUNT_KEY) || "0", 10);
  if (!dismissedAt) return true;
  const elapsed = Date.now() - parseInt(dismissedAt, 10);
  const cooldown = dismissCount >= 2 ? 30 * DAY_MS : 7 * DAY_MS;
  return elapsed >= cooldown;
}

function roleCopy(role, t) {
  const key = `push.banner.role.${role}`;
  const generic = t("push.banner.role.default");
  const specific = t(key);
  // i18next devuelve la key si no existe — fallback al genérico
  return specific === key ? generic : specific;
}

export default function PushBanner() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [visible, setVisible] = useState(() => shouldShowBanner());
  const [permission, setPermission] = useState(() => permissionState());
  const [activating, setActivating] = useState(false);

  const dismiss = () => {
    const count = parseInt(localStorage.getItem(DISMISS_COUNT_KEY) || "0", 10) + 1;
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
    localStorage.setItem(DISMISS_COUNT_KEY, count.toString());
    setVisible(false);
  };

  const activate = async () => {
    setActivating(true);
    try {
      const res = await activatePush();
      if (res.ok) {
        toast.success(t("push.activated"));
        setPermission("granted");
        setVisible(false);
        // Enviar notif de prueba (silent on UI; el SO la muestra)
        try {
          await sendTestNotification();
        } catch (e) { /* ignore */ }
      } else if (res.reason === "denied") {
        setPermission("denied");
        toast.error(t("push.denied"));
      } else {
        toast.error(t("push.activateFailed"));
      }
    } catch (e) {
      toast.error(e?.message || t("push.activateFailed"));
    } finally {
      setActivating(false);
    }
  };

  if (!visible || !user) return null;

  // Permission denied: mostrar instrucciones en lugar del CTA
  if (permission === "denied") {
    return (
      <div data-testid="push-banner-denied" className="bg-amber-50 border-b border-amber-200 px-4 py-2.5">
        <div className="max-w-7xl mx-auto flex items-center gap-3">
          <BellOff className="w-4 h-4 text-amber-700 flex-shrink-0" />
          <p className="text-xs text-amber-900 flex-1 leading-relaxed">
            {t("push.banner.deniedText")}
          </p>
          <button
            data-testid="push-banner-dismiss"
            onClick={dismiss}
            aria-label={t("common.close")}
            className="p-1.5 hover:bg-amber-100 rounded-lg text-amber-700"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  // Default state — CTA principal
  return (
    <div data-testid="push-banner" className="bg-gradient-to-r from-[#FFFBE6] to-[#FFFEF7] border-b border-[#FFDF00]/40 px-4 py-2.5">
      <div className="max-w-7xl mx-auto flex items-center gap-3">
        <div className="w-7 h-7 rounded-full bg-[#FFDF00] flex items-center justify-center flex-shrink-0">
          <Bell className="w-3.5 h-3.5 text-[#1A1A1A]" />
        </div>
        <p className="text-xs text-[#1A1A1A] flex-1 leading-relaxed">
          {roleCopy(user.role, t)}
        </p>
        <button
          data-testid="push-banner-activate"
          onClick={activate}
          disabled={activating}
          className="px-3 py-1.5 bg-[#1A1A1A] text-[#FFDF00] text-xs font-bold rounded-lg hover:bg-[#000] disabled:opacity-50 whitespace-nowrap"
        >
          {activating ? t("push.banner.activating") : t("push.banner.activate")}
        </button>
        <button
          data-testid="push-banner-later"
          onClick={dismiss}
          className="text-xs text-slate-600 hover:text-[#1A1A1A] underline decoration-dotted px-2 whitespace-nowrap"
        >
          {t("push.banner.later")}
        </button>
        <button
          data-testid="push-banner-dismiss"
          onClick={dismiss}
          aria-label={t("common.close")}
          className="p-1 hover:bg-white/60 rounded-lg text-slate-500"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
